import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { fetchAdminFeedback } from "../features/admin/slice-extensions/admin-slice-extension";
import {
  selectAdminFeedback,
  selectAdminFeedbackStatus,
  selectAdminFeedbackTotalCount,
  selectAdminFeedbackTotalPages,
} from "../features/admin/admin-slice";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminTabs from "../components/admin/AdminTabs";
import AdminFeedbackRow from "../components/admin/AdminFeedbackRow";

const PAGE_SIZE = 20;

/** The cross-account list of wishes and issue reports instructors sent us. */
const AdminFeedbackPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const entries = useAppSelector(selectAdminFeedback);
  const status = useAppSelector(selectAdminFeedbackStatus);
  const totalCount = useAppSelector(selectAdminFeedbackTotalCount);
  const totalPages = useAppSelector(selectAdminFeedbackTotalPages);

  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(fetchAdminFeedback({ page, pageSize: PAGE_SIZE }));
  }, [dispatch, page]);

  const isLoading = status === LoadingStatus.Loading;
  const isFailed = status === LoadingStatus.Failed;

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[1280px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6 lg:space-y-6">
              <div>
                <h1>{t("admin.feedback.title")}</h1>
                <p className="text-grey-40 dark:text-grey-70">{t("admin.feedback.subtitle")}</p>
              </div>

              <AdminTabs />

              {isFailed && (
                <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
                  <p>{t("admin.feedback.errorLoading")}</p>
                </div>
              )}

              {isLoading && entries.length === 0 ? (
                <p className="py-10 text-center text-grey-40 dark:text-grey-70">
                  {t("admin.loading")}
                </p>
              ) : entries.length === 0 && !isFailed ? (
                <div className="with-border rounded-2xl p-8 text-center text-grey-40 dark:text-grey-70">
                  {t("admin.feedback.empty")}
                </div>
              ) : (
                <>
                  <p className="text-sm text-grey-50 dark:text-grey-60">
                    {t("admin.feedback.totalEntries", { count: totalCount })}
                  </p>
                  <div className="space-y-2 lg:space-y-4">
                    {entries.map((entry) => (
                      <AdminFeedbackRow key={entry.id} entry={entry} />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-2">
                      <button
                        className="btn btn-secondary"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        {t("admin.previous")}
                      </button>
                      <span className="text-sm text-grey-50 dark:text-grey-60">
                        {t("admin.pageOf", { page, totalPages })}
                      </span>
                      <button
                        className="btn btn-secondary"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        {t("admin.next")}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminFeedbackPage;
